import React, { useRef, useEffect, useMemo, useCallback } from "react";
import { Container, Graphics, Sprite, Text } from "@pixi/react";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import * as PIXI from "pixi.js";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import {
  closeSettings,
  closeMenu,
  setSpacebarSpinEnabled,
  toggleBackgroundMusic,
  toggleSoundEffects,
  setMasterVolume,
  setAutoplaySpeed,
  setPendingAutoplaySpeed,
} from "../../../../State/actions";
import {
  selectSettingsActive,
  selectSpacebarSpinEnabled,
  selectMasterVolume,
  selectAutoPlaySpeed,
  selectPendingAutoPlaySpeed,
  selectAutoPlayActive,
  selectIsSpinning,
} from "../../../../State/selectors";
import { ASSET_NAME } from "../../../../assets";
import { SOUND_NAME } from "../../../../sounds";
import { PixiSettings } from "../../../../pixiSettings";
import {
  USER_INTERFACE_BUTTON_CONFIG,
  FONT_CONFIG,
} from "../../../../textureConfigs";
import ButtonMenuClose from "../Buttons/ButtonMenuClose";
import Checkbox from "../Buttons/Checkbox";
import VolumeBar from "../Buttons/VolumeBar";
import { soundManager } from "../../../../SoundManager";
import anime from "animejs";

const Settings = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const settingsActive = useSelector(selectSettingsActive);
  const {
    dimensions,
    device,
    orientation,
    getResponsiveConfig,
    uiSymbolScale,
  } = useDimensions();
  const containerRef = useRef(null);
  const animationRef = useRef(null);

  // Get spacebar spin setting from Redux
  const spacebarSpinEnabled = useSelector(selectSpacebarSpinEnabled);

  // Get sound settings from Redux
  const { soundEffectsEnabled, backgroundMusicEnabled } = useSelector(
    (state) => state.sound
  );
  const masterVolume = useSelector(selectMasterVolume);

  // Get turbo spin settings from Redux
  const autoPlaySpeed = useSelector(selectAutoPlaySpeed);
  const pendingAutoPlaySpeed = useSelector(selectPendingAutoPlaySpeed);
  const autoPlayActive = useSelector(selectAutoPlayActive);
  const isSpinning = useSelector(selectIsSpinning);

  // Memoize device/orientation checks
  const isDesktop = useMemo(
    () => device === PixiSettings.DEVICE.DESKTOP,
    [device]
  );
  const isMobileLandscape = useMemo(
    () =>
      device === PixiSettings.DEVICE.MOBILE &&
      orientation === PixiSettings.ORIENTATION.LANDSCAPE,
    [device, orientation]
  );

  // Determine current effective speed (pending takes precedence if set) - memoized
  const currentSpeed = useMemo(
    () => pendingAutoPlaySpeed || autoPlaySpeed,
    [pendingAutoPlaySpeed, autoPlaySpeed]
  );
  const turboSpinEnabled = useMemo(
    () => currentSpeed === "turbo",
    [currentSpeed]
  );

  // Memoize popup frame texture lookup
  const popupFrameTexture = useMemo(
    () => PIXI.Assets.get(ASSET_NAME.popup_frame_settings),
    []
  );

  // Determine if should show popup frame (not fullscreen) - memoized
  const showPopupFrame = useMemo(
    () =>
      isDesktop ||
      (device === PixiSettings.DEVICE.TABLET &&
        orientation === PixiSettings.ORIENTATION.LANDSCAPE),
    [isDesktop, device, orientation]
  );

  const handleClose = useCallback(() => {
    dispatch(closeSettings());
    dispatch(closeMenu());
  }, [dispatch]);

  // Turbo spin toggle handler - memoized
  const handleTurboSpinToggle = useCallback(
    (value) => {
      // Toggle between "auto" and "turbo" speed
      const newSpeed = value ? "turbo" : "auto";

      // If we're in the middle of a spin or autoplay is active, set as pending
      if (isSpinning || autoPlayActive) {
        dispatch(setPendingAutoplaySpeed(newSpeed));
      } else {
        // Apply immediately if no spin is active
        dispatch(setAutoplaySpeed(newSpeed));
      }
    },
    [dispatch, isSpinning, autoPlayActive]
  );

  // Sound toggle handlers - memoized
  const handleMusicToggle = useCallback(
    (value) => {
      soundManager.play(SOUND_NAME.button_click);
      dispatch(toggleBackgroundMusic());
    },
    [dispatch]
  );

  const handleSoundFXToggle = useCallback(
    (value) => {
      soundManager.play(SOUND_NAME.button_click);
      dispatch(toggleSoundEffects());
    },
    [dispatch]
  );

  // Spacebar toggle handler - memoized
  const handleSpacebarToggle = useCallback(
    (value) => {
      dispatch(setSpacebarSpinEnabled(value));
    },
    [dispatch]
  );

  // Volume change handler - maps slider value (0-1) to volume (0-2) - memoized
  const handleVolumeChange = useCallback(
    (sliderValue) => {
      const actualVolume = sliderValue * 2;
      dispatch(setMasterVolume(actualVolume));
      soundManager.setMasterVolume(actualVolume);
    },
    [dispatch]
  );

  useEffect(() => {
    if (containerRef.current) {
      if (settingsActive) {
        animationRef.current = anime({
          targets: containerRef.current,
          alpha: 1,
          duration: 200,
          easing: "easeOutQuad",
        });
      } else {
        animationRef.current = anime({
          targets: containerRef.current,
          alpha: 0,
          duration: 200,
          easing: "easeInQuad",
        });
      }
    }
  }, [settingsActive]);

  // Calculate popup size: responsive based on viewport dimensions with max size cap
  const popupSize = useMemo(() => {
    if (!popupFrameTexture) {
      return { width: 0, height: 0 };
    }

    // Get native texture dimensions
    const nativeWidth = popupFrameTexture.width;
    const nativeHeight = popupFrameTexture.height;
    const aspectRatio = nativeWidth / nativeHeight;

    if (showPopupFrame) {
      // Desktop & Tablet Landscape: Scale to fit viewport with padding
      const maxWidth = dimensions.width * 0.535; // 90% of screen width
      const maxHeight = dimensions.height * 0.535; // 90% of screen height

      // Calculate size based on aspect ratio to fit within max dimensions
      let width, height;
      if (maxWidth / aspectRatio <= maxHeight) {
        // Width is the limiting factor
        width = maxWidth;
        height = maxWidth / aspectRatio;
      } else {
        // Height is the limiting factor
        height = maxHeight;
        width = maxHeight * aspectRatio;
      }

      // Cap at native size with multiplier (don't exceed this size)
      const maxNativeSize = nativeWidth * 0.535;
      if (width > maxNativeSize) {
        width = maxNativeSize;
        height = maxNativeSize / aspectRatio;
      }

      return { width, height };
    }

    // Mobile & Tablet Portrait: Use native size with multiplier (fullscreen background)
    const multiplier = 0.7;
    return {
      width: nativeWidth * multiplier,
      height: nativeHeight * multiplier,
    };
  }, [popupFrameTexture, showPopupFrame, dimensions]);

  // Calculate viewport configuration relative to parent container with device-specific padding
  const viewportConfig = useMemo(() => {
    if (!popupFrameTexture || popupSize.width === 0) {
      return {
        maxWidth: 0,
        maxHeight: 0,
        paddingHorizontal: 0,
        paddingVertical: 0,
        x: 0,
        y: 0,
      };
    }

    // Get viewport configuration based on device and orientation
    const getViewportConfig = () => {
      if (showPopupFrame) {
        // Desktop & Tablet Landscape: Use same padding configuration
        return {
          paddingHorizontalRatio: 0.04,
          paddingVerticalRatio: 0.04,
        };
      }

      // Mobile & Tablet Portrait (fullscreen)
      if (device === PixiSettings.DEVICE.MOBILE) {
        if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
          // Mobile Portrait
          return {
            paddingHorizontalRatio: 0.09,
            paddingVerticalRatio: 0.19,
          };
        } else {
          // Mobile Landscape
          return {
            paddingHorizontalRatio: 0.165,
            paddingVerticalRatio: 0.125,
          };
        }
      } else {
        // Tablet Portrait
        return {
          paddingHorizontalRatio: 0.17,
          paddingVerticalRatio: 0.125,
        };
      }
    };

    const config = getViewportConfig();

    if (showPopupFrame) {
      // Desktop & Tablet Landscape: Calculate padding based on popup frame sprite dimensions
      const paddingHorizontal = popupSize.width * config.paddingHorizontalRatio;
      const paddingVertical = popupSize.height * config.paddingVerticalRatio;

      return {
        maxWidth: popupSize.width - paddingHorizontal * 2,
        maxHeight: popupSize.height - paddingVertical * 2,
        paddingHorizontal,
        paddingVertical,
        x: -popupSize.width / 2 + paddingHorizontal,
        y: -popupSize.height / 2 + paddingVertical,
      };
    } else {
      // Mobile & Tablet Portrait: Calculate padding based on full-screen background dimensions
      const paddingHorizontal =
        dimensions.width * config.paddingHorizontalRatio;
      const paddingVertical = dimensions.height * config.paddingVerticalRatio;

      return {
        maxWidth: dimensions.width - paddingHorizontal * 2,
        maxHeight: dimensions.height - paddingVertical * 2,
        paddingHorizontal,
        paddingVertical,
        x: -dimensions.width / 2 + paddingHorizontal,
        y: -dimensions.height / 2 + paddingVertical,
      };
    }
  }, [
    device,
    orientation,
    popupSize,
    dimensions,
    popupFrameTexture,
    showPopupFrame,
  ]);

  // Calculate responsive font sizes and layout multipliers
  const layoutConfig = useMemo(() => {
    const baseTitleSize = 36;
    const baseCheckboxLabelSize = 26;
    let titleMultiplier = 1.0;
    let checkboxLabelMultiplier = 1.0;
    let leftPaddingMultiplier = 1.0; // Multiplier for buttonConfig.width
    let titleToCheckboxSpacing = 1.3; // Gap from title to first checkbox (in itemSpacing units)
    let checkboxToCheckboxSpacing = 1.0; // Gap between checkboxes (in itemSpacing units)
    let sectionSpacing = 1.5; // Gap between sections (in itemSpacing units)

    if (device === PixiSettings.DEVICE.MOBILE) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        titleMultiplier = 2.5;
        checkboxLabelMultiplier = 2.2;
        leftPaddingMultiplier = 0.4;
        titleToCheckboxSpacing = 1.1;
        checkboxToCheckboxSpacing = 0.65;
        sectionSpacing = 0.75;
      } else {
        titleMultiplier = 2.25;
        checkboxLabelMultiplier = 2.05;
        leftPaddingMultiplier = 1.0;
        titleToCheckboxSpacing = 1.75;
        checkboxToCheckboxSpacing = 1.25;
        sectionSpacing = 1.5;
      }
    } else if (device === PixiSettings.DEVICE.TABLET) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        titleMultiplier = 3.0;
        checkboxLabelMultiplier = 2.5;
        leftPaddingMultiplier = 0.5;
        titleToCheckboxSpacing = 1.1;
        checkboxToCheckboxSpacing = 0.75;
        sectionSpacing = 1.0;
      } else {
        titleMultiplier = 1.7;
        checkboxLabelMultiplier = 1.7;
        leftPaddingMultiplier = 1.0;
        titleToCheckboxSpacing = 1.5;
        checkboxToCheckboxSpacing = 1.2;
        sectionSpacing = 1.3;
      }
    } else {
      // Desktop
      leftPaddingMultiplier = 1.0;
      titleToCheckboxSpacing = 1.3;
      checkboxToCheckboxSpacing = 1.1;
      sectionSpacing = 1.0;
    }

    return {
      titleFontSize: Math.round(
        baseTitleSize * titleMultiplier * uiSymbolScale
      ),
      checkboxLabelFontSize: Math.round(
        baseCheckboxLabelSize * checkboxLabelMultiplier * uiSymbolScale
      ),
      leftPaddingMultiplier,
      titleToCheckboxSpacing,
      checkboxToCheckboxSpacing,
      sectionSpacing,
    };
  }, [device, orientation, uiSymbolScale]);

  // Text style - same for all titles (Settings, Audio, Volume)
  const sectionStyle = useMemo(
    () =>
      new PIXI.TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: layoutConfig.titleFontSize,
        fontWeight: "bold",
        fill: 0xffffff, // White color
        align: "left",
      }),
    [layoutConfig.titleFontSize]
  );

  // Get responsive button configuration
  const buttonConfig = useMemo(() => {
    return getResponsiveConfig(USER_INTERFACE_BUTTON_CONFIG);
  }, [getResponsiveConfig]);

  // Calculate vertical spacing for items
  const itemSpacing = useMemo(() => {
    return viewportConfig.maxHeight * 0.12; // 12% of viewport height
  }, [viewportConfig.maxHeight]);

  // Calculate left padding using buttonConfig.width with device-specific multiplier
  const leftPadding = useMemo(() => {
    return buttonConfig.width * layoutConfig.leftPaddingMultiplier;
  }, [buttonConfig.width, layoutConfig.leftPaddingMultiplier]);

  // Get checkbox size to align titles with checkbox left edge
  const checkboxSize = useMemo(() => {
    const buttonConfig = getResponsiveConfig(USER_INTERFACE_BUTTON_CONFIG);
    return buttonConfig.width * 0.7; // Using 0.7 multiplier (same as checkboxes)
  }, [getResponsiveConfig]);

  // Calculate title x position to align with checkbox left edge
  // Checkboxes are centered at leftPadding, so left edge is at leftPadding - checkboxSize/2
  const titleX = useMemo(() => {
    return leftPadding - checkboxSize / 2;
  }, [leftPadding, checkboxSize]);

  // Calculate column positions for mobile landscape two-column layout
  const columnPositions = useMemo(() => {
    if (!isMobileLandscape) {
      return { leftColumn: 0, rightColumn: 0 };
    }

    // Split viewport into two columns
    const columnWidth = viewportConfig.maxWidth / 2;
    return {
      leftColumn: 0, // Settings column (left side)
      rightColumn: columnWidth, // Audio/Volume column (right side)
    };
  }, [isMobileLandscape, viewportConfig.maxWidth]);

  // Calculate Y positions using layoutConfig spacing values
  const yPositions = useMemo(() => {
    if (isMobileLandscape) {
      // Mobile Landscape: Two columns with independent Y positions
      // Left column: Settings section
      let leftY = 0.5;
      const settingsTitleY = leftY;
      leftY += layoutConfig.titleToCheckboxSpacing;
      const turboSpinY = leftY;

      // Right column: Audio/Volume section
      let rightY = 0.5;
      const audioTitleY = rightY;
      rightY += layoutConfig.titleToCheckboxSpacing * 0.85; // Slightly reduced spacing

      // Volume bar is directly under Audio title
      const volumeBarY = rightY;
      rightY += layoutConfig.checkboxToCheckboxSpacing * 0.5; // Reduced spacing

      // Volume title is under the volume bar
      const volumeTitleY = rightY;
      rightY += layoutConfig.titleToCheckboxSpacing;

      const musicY = rightY;
      rightY += layoutConfig.checkboxToCheckboxSpacing;
      const soundFXY = rightY;

      return {
        settingsTitleY,
        turboSpinY,
        spacebarY: null, // Not used on mobile
        audioTitleY,
        volumeBarY,
        volumeTitleY,
        musicY,
        soundFXY,
      };
    }

    // Single column layout (Desktop, Tablet, Mobile Portrait)
    let currentY = 0.5; // Starting position for first title

    // Settings title
    const settingsTitleY = currentY;
    currentY += layoutConfig.titleToCheckboxSpacing;

    // Turbospin checkbox
    const turboSpinY = currentY;

    // Spacebar checkbox (desktop only)
    let spacebarY;
    if (isDesktop) {
      currentY += layoutConfig.checkboxToCheckboxSpacing;
      spacebarY = currentY;
      currentY += layoutConfig.sectionSpacing * 0.85; // Slightly reduced spacing
    } else {
      // On mobile/tablet, skip spacebar and go directly to Audio section
      currentY += layoutConfig.sectionSpacing * 0.7; // Slightly reduced spacing
      spacebarY = null; // Not used on mobile/tablet
    }

    // Audio title
    const audioTitleY = currentY;
    currentY += layoutConfig.titleToCheckboxSpacing * 0.8; // Slightly reduced spacing

    // Volume bar is directly under Audio title
    const volumeBarY = currentY;
    currentY += layoutConfig.checkboxToCheckboxSpacing * 0.5; // Reduced spacing

    // Volume title is under the volume bar
    const volumeTitleY = currentY;
    currentY += layoutConfig.titleToCheckboxSpacing;

    // Music checkbox
    const musicY = currentY;
    currentY += layoutConfig.checkboxToCheckboxSpacing;

    // Sound FX checkbox
    const soundFXY = currentY;

    return {
      settingsTitleY,
      turboSpinY,
      spacebarY,
      audioTitleY,
      volumeBarY,
      volumeTitleY,
      musicY,
      soundFXY,
    };
  }, [layoutConfig, isDesktop, isMobileLandscape]);

  // Calculate close button position (top right of viewport) - absolute coordinates
  const closeButtonPosition = useMemo(() => {
    if (!viewportConfig || viewportConfig.maxWidth === 0) {
      return { x: 0, y: 0 };
    }

    // Position at top right corner with some padding (relative to viewport)
    const relativeX = viewportConfig.maxWidth - buttonConfig.width * 0.25;
    const relativeY = buttonConfig.height * 0.25;

    // Convert to absolute coordinates (viewport is inside popup container which is centered)
    return {
      x: dimensions.width / 2 + viewportConfig.x + relativeX,
      y: dimensions.height / 2 + viewportConfig.y + relativeY,
    };
  }, [viewportConfig, buttonConfig, dimensions]);

  // Memoize Graphics draw functions
  const drawBackground = useCallback(
    (g) => {
      g.clear();
      g.beginFill(0x000000, 1);
      g.drawRect(0, 0, dimensions.width, dimensions.height);
      g.endFill();
    },
    [dimensions.width, dimensions.height]
  );

  const drawPopupBackground = useCallback(
    (g) => {
      g.clear();
      g.beginFill(0x161616, 1); // #161616 color
      g.drawRect(
        -dimensions.width / 2,
        -dimensions.height / 2,
        dimensions.width,
        dimensions.height
      );
      g.endFill();
    },
    [dimensions.width, dimensions.height]
  );

  // Memoize popup container position (centered)
  const popupContainerPosition = useMemo(
    () => ({
      x: dimensions.width / 2,
      y: dimensions.height / 2,
    }),
    [dimensions.width, dimensions.height]
  );

  // Memoize translation texts
  const settingsTitleText = useMemo(() => t("ui_settings_title"), [t]);
  const audioTitleText = useMemo(() => t("ui_settings_audio"), [t]);
  const volumeTitleText = useMemo(() => t("ui_settings_volume"), [t]);

  // Don't render if texture not loaded
  if (!popupFrameTexture) {
    return null;
  }

  return (
    <Container
      ref={containerRef}
      name="secondary_settings_overlay"
      zIndex={10}
      eventMode={settingsActive ? "static" : "none"}
    >
      <Graphics
        name="secondary_settings_background"
        draw={drawBackground}
        alpha={0.5}
        eventMode={settingsActive ? "static" : "none"}
        onpointertap={handleClose}
      />

      {/* Settings Popup Frame - Centered */}
      <Container
        name="secondary_settings_popup"
        x={popupContainerPosition.x}
        y={popupContainerPosition.y}
      >
        {/* Background Graphics for Mobile & Tablet Portrait - covers full screen */}
        {!showPopupFrame && (
          <Graphics
            name="secondary_settings_popup_background"
            draw={drawPopupBackground}
          />
        )}

        {/* Popup Frame Sprite - centered, maintains aspect ratio */}
        <Sprite
          texture={popupFrameTexture}
          anchor={0.5}
          width={popupSize.width}
          height={popupSize.height}
        />

        {/* Content Viewport Container - positioned relative to parent container */}
        <Container
          name="secondary_settings_content_viewport"
          x={viewportConfig.x}
          y={viewportConfig.y}
        >
          {/* Settings Title */}
          <Text
            text={settingsTitleText}
            style={sectionStyle}
            anchor={{ x: 0, y: 0 }}
            x={Math.round(titleX + columnPositions.leftColumn)}
            y={Math.round(itemSpacing * yPositions.settingsTitleY)}
            resolution={2}
          />

          {/* Turbospin Checkbox */}
          <Checkbox
            translationKey="ui_settings_turbo"
            checked={turboSpinEnabled}
            onChange={handleTurboSpinToggle}
            x={Math.round(leftPadding + columnPositions.leftColumn)}
            y={Math.round(itemSpacing * yPositions.turboSpinY)}
            sizeMultiplier={0.65}
            labelFontSize={layoutConfig.checkboxLabelFontSize}
          />

          {/* Spacebar to spin Checkbox - Desktop only */}
          {isDesktop && (
            <Checkbox
              translationKey="ui_settings_spacebar"
              checked={spacebarSpinEnabled}
              onChange={handleSpacebarToggle}
              x={Math.round(leftPadding + columnPositions.leftColumn)}
              y={Math.round(itemSpacing * yPositions.spacebarY)}
              sizeMultiplier={0.65}
              labelFontSize={layoutConfig.checkboxLabelFontSize}
            />
          )}

          {/* Audio Section Title */}
          <Text
            text={audioTitleText}
            style={sectionStyle}
            anchor={{ x: 0, y: 0 }}
            x={Math.round(titleX + columnPositions.rightColumn)}
            y={Math.round(itemSpacing * yPositions.audioTitleY)}
            resolution={2}
          />

          {/* Volume Slider - positioned directly below Audio title */}
          <VolumeBar
            x={Math.round(titleX + columnPositions.rightColumn)}
            y={Math.round(itemSpacing * yPositions.volumeBarY)}
            value={masterVolume / 2}
            onChange={handleVolumeChange}
            buttonConfig={buttonConfig}
          />

          {/* Volume Label - positioned below volume bar */}
          <Text
            text={volumeTitleText}
            style={sectionStyle}
            anchor={{ x: 0, y: 0 }}
            x={Math.round(titleX + columnPositions.rightColumn)}
            y={Math.round(itemSpacing * yPositions.volumeTitleY)}
            resolution={2}
          />

          {/* Music Checkbox */}
          <Checkbox
            translationKey="ui_settings_music"
            checked={backgroundMusicEnabled}
            onChange={handleMusicToggle}
            x={Math.round(leftPadding + columnPositions.rightColumn)}
            y={Math.round(itemSpacing * yPositions.musicY)}
            sizeMultiplier={0.7}
            labelFontSize={layoutConfig.checkboxLabelFontSize}
          />

          {/* Sound FX Checkbox */}
          <Checkbox
            translationKey="ui_settings_sound"
            checked={soundEffectsEnabled}
            onChange={handleSoundFXToggle}
            x={Math.round(leftPadding + columnPositions.rightColumn)}
            y={Math.round(itemSpacing * yPositions.soundFXY)}
            sizeMultiplier={0.7}
            labelFontSize={layoutConfig.checkboxLabelFontSize}
          />
        </Container>
      </Container>

      {/* Close Button */}
      <ButtonMenuClose
        onClose={handleClose}
        x={closeButtonPosition.x}
        y={closeButtonPosition.y}
        isSettings={true}
        sizeMultiplier={0.65}
      />
    </Container>
  );
};

export default Settings;
